import React, { useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { Users, CheckCircle, ClipboardList, DollarSign, Plus, X } from 'lucide-react';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const CustomerManagement = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [customerForm, setCustomerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    preferredContact: 'email',
    status: 'active',
    notes: '',
    propertyType: 'residential',
    propertySize: '',
    source: 'website',
    services: {
      'Lawn Mowing': '',
      'Hedge Trimming': '',
      'Tree Trimming': '',
      'Fertilizing': '',
      'Pre-Emergent / Fertilize': '',
      'Weed Control': '',
      'Aeration': '',
      'Seeding': '',
      'Lighting': '',
      'Planting': '',
      'Other': ''
    }
  });

  // Real-time listeners
  useEffect(() => {
    const unsubscribers = [];

    // Customers listener
    const customersQuery = query(
      collection(db, 'customers'),
      orderBy('createdAt', 'desc')
    );
    const customersUnsub = onSnapshot(customersQuery, (snapshot) => {
      const customersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    });
    unsubscribers.push(customersUnsub);

    // Service History listener
    const serviceHistoryUnsub = onSnapshot(
      collection(db, 'service_history'),
      (snapshot) => {
        const historyData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setServiceHistory(historyData);
      }
    );
    unsubscribers.push(serviceHistoryUnsub);

    // Services listener
    const servicesUnsub = onSnapshot(
      collection(db, 'services'),
      (snapshot) => {
        const servicesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setServices(servicesData);
      }
    );
    unsubscribers.push(servicesUnsub);

    setIsLoading(false);

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  // Customer Management Functions
  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const customerData = {
        ...customerForm,
        fullName: `${customerForm.firstName} ${customerForm.lastName}`,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      };

      if (editingCustomer) {
        await updateDoc(doc(db, 'customers', editingCustomer.id), customerData);
      } else {
        await addDoc(collection(db, 'customers'), {
          ...customerData,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          totalServices: 0,
          totalSpent: 0,
          lastService: null
        });
      }

      resetCustomerForm();
    } catch (error) {
      console.error('Error managing customer:', error);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer? This will also delete their service history.')) {
      try {
        await deleteDoc(doc(db, 'customers', customerId));
        
        // Delete associated service history
        const customerServices = serviceHistory.filter(s => s.customerId === customerId);
        for (const service of customerServices) {
          await deleteDoc(doc(db, 'service_history', service.id));
        }
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const resetCustomerForm = () => {
    setEditingCustomer(null);
    setShowForm(false);
    setCustomerForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      preferredContact: 'email',
      status: 'active',
      notes: '',
      propertyType: 'residential',
      propertySize: '',
      source: 'website',
      services: {
        'Lawn Mowing': '',
        'Hedge Trimming': '',
        'Tree Trimming': '',
        'Fertilizing': '',
        'Pre-Emergent / Fertilize': '',
        'Weed Control': '',
        'Aeration': '',
        'Seeding': '',
        'Lighting': '',
        'Planting': '',
        'Other': ''
      }
    });
  };

  const editCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      preferredContact: customer.preferredContact || 'email',
      status: customer.status || 'active',
      notes: customer.notes || '',
      propertyType: customer.propertyType || 'residential',
      propertySize: customer.propertySize || '',
      source: customer.source || 'website',
      services: customer.services || {
        'Lawn Mowing': '',
        'Hedge Trimming': '',
        'Tree Trimming': '',
        'Fertilizing': '',
        'Pre-Emergent / Fertilize': '',
        'Weed Control': '',
        'Aeration': '',
        'Seeding': '',
        'Lighting': '',
        'Planting': '',
        'Other': ''
      }
    });
    setShowForm(true);
  };

  const getCustomerServices = (customerId) => {
    return serviceHistory.filter(service => service.customerId === customerId);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      prospect: 'bg-blue-100 text-blue-800',
      archived: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.active;
  };

  const getServiceStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.scheduled;
  };

  const filteredCustomers = customers.filter(customer => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) {
      const matchesFilter = filterStatus === 'all' || customer.status === filterStatus;
      return matchesFilter;
    }
    const name = (customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '').toLowerCase();
    const matchesSearch = name.includes(q) ||
                         (customer.firstName && customer.firstName.toLowerCase().includes(q)) ||
                         (customer.lastName && customer.lastName.toLowerCase().includes(q)) ||
                         (customer.email && customer.email.toLowerCase().includes(q)) ||
                         (customer.phone && String(customer.phone).includes(searchTerm));
    const matchesFilter = filterStatus === 'all' || customer.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Customer Management
          </h1>
          <p className="text-secondary-600">
            Manage customer profiles, service history, and relationships
          </p>
        </div>

        {/* Quick search - find customer fast */}
        <div className="card p-4 mb-6">
          <label className="block text-sm font-medium text-secondary-700 mb-2">Quick search</label>
          <input
            type="text"
            placeholder="Search by name, email, or phone to find a customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full text-base py-3 min-h-[48px]"
            autoComplete="off"
          />
          {searchTerm && (
            <p className="text-sm text-secondary-500 mt-2">
              Showing {filteredCustomers.length} of {customers.length} customers
            </p>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">Total Customers</p>
                  <p className="text-3xl font-bold text-secondary-900">{customers.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-primary-500 text-white">
                  <Users className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">Active Customers</p>
                  <p className="text-3xl font-bold text-secondary-900">
                    {customers.filter(c => c.status === 'active').length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-500 text-white">
                  <CheckCircle className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">Total Services</p>
                  <p className="text-3xl font-bold text-secondary-900">{serviceHistory.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-accent-500 text-white">
                  <ClipboardList className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
                  <p className="text-3xl font-bold text-secondary-900">
                    ${customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-secondary-500 text-white">
                  <DollarSign className="w-6 h-6" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="flex space-x-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="prospect">Prospect</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn-primary whitespace-nowrap"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Customer
            </button>
          </div>
        </div>

        {/* Customer Form */}
        {showForm && (
          <div className="mb-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </h3>
                <form onSubmit={handleCustomerSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        value={customerForm.firstName}
                        onChange={(e) => setCustomerForm({...customerForm, firstName: e.target.value})}
                        className="input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        value={customerForm.lastName}
                        onChange={(e) => setCustomerForm({...customerForm, lastName: e.target.value})}
                        className="input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        value={customerForm.email}
                        onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                        className="input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone *</label>
                      <input
                        type="tel"
                        value={customerForm.phone}
                        onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                        className="input"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Street Address *</label>
                    <input
                      type="text"
                      value={customerForm.address.street}
                      onChange={(e) => setCustomerForm({
                        ...customerForm,
                        address: { ...customerForm.address, street: e.target.value }
                      })}
                      className="input"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        value={customerForm.address.city}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: { ...customerForm.address, city: e.target.value }
                        })}
                        className="input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State *</label>
                      <input
                        type="text"
                        value={customerForm.address.state}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: { ...customerForm.address, state: e.target.value }
                        })}
                        className="input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Zip Code *</label>
                      <input
                        type="text"
                        value={customerForm.address.zipCode}
                        onChange={(e) => setCustomerForm({
                          ...customerForm,
                          address: { ...customerForm.address, zipCode: e.target.value }
                        })}
                        className="input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="form-group">
                      <label className="form-label">Preferred Contact</label>
                      <select
                        value={customerForm.preferredContact}
                        onChange={(e) => setCustomerForm({...customerForm, preferredContact: e.target.value})}
                        className="input"
                      >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="text">Text</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select
                        value={customerForm.status}
                        onChange={(e) => setCustomerForm({...customerForm, status: e.target.value})}
                        className="input"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="prospect">Prospect</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Property Type</label>
                      <select
                        value={customerForm.propertyType}
                        onChange={(e) => setCustomerForm({...customerForm, propertyType: e.target.value})}
                        className="input"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="industrial">Industrial</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Property Size</label>
                      <input
                        type="text"
                        value={customerForm.propertySize}
                        onChange={(e) => setCustomerForm({...customerForm, propertySize: e.target.value})}
                        className="input"
                        placeholder="e.g., 0.5 acres"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea
                      value={customerForm.notes}
                      onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})}
                      className="input"
                      rows="3"
                      placeholder="Additional notes about the customer..."
                    />
                  </div>

                  {/* Services Section */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Services & Pricing</h3>
                    <div className="space-y-4">
                      <div className="text-sm text-secondary-600 mb-3">
                        Configure pricing for services provided to this customer
                      </div>

                      {Object.entries(customerForm.services).map(([serviceType, price]) => (
                        <div key={serviceType} className="border border-secondary-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-secondary-900">{serviceType}</h4>
                            </div>
                            <div className="w-32">
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-500 text-sm">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={price}
                                  onChange={(e) => setCustomerForm({
                                    ...customerForm,
                                    services: {
                                      ...customerForm.services,
                                      [serviceType]: e.target.value
                                    }
                                  })}
                                  className="input pl-6 text-sm"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button type="submit" className="btn-primary">
                      {editingCustomer ? 'Update' : 'Create'} Customer
                    </button>
                    <button 
                      type="button" 
                      onClick={resetCustomerForm}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Customer List */}
        <div className="grid grid-cols-1 gap-4">
          {filteredCustomers.map((customer) => (
            <div key={customer.id} className="card-hover">
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-secondary-900">
                        {customer.fullName}
                      </h3>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(customer.status)}`}>
                        {customer.status}
                      </span>
                      <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded">
                        {customer.propertyType}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-secondary-600">Contact</p>
                        <p className="text-secondary-900">{customer.email}</p>
                        <p className="text-secondary-900">{customer.phone}</p>
                      </div>
                      <div>
                        <p className="text-sm text-secondary-600">Address</p>
                        <p className="text-secondary-900">
                          {typeof customer.address === 'object' && customer.address 
                            ? `${customer.address.street}, ${customer.address.city}, ${customer.address.state} ${customer.address.zipCode}`
                            : customer.address || 'No address provided'
                          }
                        </p>
                        {customer.city && customer.state && customer.zipCode && (
                          <p className="text-secondary-900">
                            {customer.city}, {customer.state} {customer.zipCode}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-secondary-600">Service Stats</p>
                        <p className="text-secondary-900">
                          Services: {customer.totalServices || 0}
                        </p>
                        <p className="text-secondary-900">
                          Total Spent: ${(customer.totalSpent || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {customer.notes && (
                      <p className="text-secondary-700 text-sm italic mb-3">
                        "{customer.notes}"
                      </p>
                    )}

                    {/* Recent Services */}
                    <div className="mt-4">
                      <p className="text-sm font-medium text-secondary-700 mb-2">Recent Services:</p>
                      <div className="flex flex-wrap gap-2">
                        {getCustomerServices(customer.id).slice(0, 3).map((service) => (
                          <span
                            key={service.id}
                            className={`px-2 py-1 rounded text-xs ${getServiceStatusColor(service.status)}`}
                          >
                            {service.serviceType} - {service.status}
                          </span>
                        ))}
                        {getCustomerServices(customer.id).length > 3 && (
                          <span className="text-xs text-secondary-500">
                            +{getCustomerServices(customer.id).length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => setSelectedCustomer(customer)}
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => editCustomer(customer)}
                      className="text-secondary-600 hover:text-secondary-700 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCustomer(customer.id)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCustomers.length === 0 && (
          <div className="card">
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="w-8 h-8 text-secondary-400" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">No Customers Found</h3>
              <p className="text-secondary-600">
                {searchTerm || filterStatus !== 'all' 
                  ? 'No customers match your search criteria.' 
                  : 'Get started by adding your first customer.'}
              </p>
            </div>
          </div>
        )}

        {/* Services Management Section */}
        <div className="mt-12">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-secondary-900 mb-2">Services Catalog</h2>
            <p className="text-secondary-600">Manage the services you provide and their base pricing</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div key={service.id} className="card">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-secondary-900">{service.name}</h3>
                      <p className="text-sm text-secondary-600 mt-1">{service.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary-600">${service.basePrice}</div>
                      <div className="text-xs text-secondary-500">base price</div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Category:</span>
                      <span className="font-medium">{service.category || 'General'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Duration:</span>
                      <span className="font-medium">{service.estimatedDuration || 'Varies'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {services.length === 0 && (
              <div className="col-span-full card">
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-secondary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Plus className="w-8 h-8 text-secondary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">No Services Configured</h3>
                  <p className="text-secondary-600 mb-4">
                    Add services to set up your pricing catalog and enable custom pricing per customer.
                  </p>
                  <button
                    onClick={() => {
                      // Add sample services
                      const sampleServices = [
                        {
                          name: 'Lawn Mowing',
                          description: 'Complete lawn mowing service including trimming',
                          basePrice: 75,
                          category: 'Lawn Care',
                          estimatedDuration: '1-2 hours'
                        },
                        {
                          name: 'Hedge Trimming',
                          description: 'Professional hedge and shrub trimming',
                          basePrice: 120,
                          category: 'Landscaping',
                          estimatedDuration: '2-3 hours'
                        },
                        {
                          name: 'Fertilizer Application',
                          description: 'Seasonal fertilizer application for healthy lawn growth',
                          basePrice: 95,
                          category: 'Lawn Care',
                          estimatedDuration: '1 hour'
                        }
                      ];

                      sampleServices.forEach(async (service) => {
                        await addDoc(collection(db, 'services'), {
                          ...service,
                          createdAt: serverTimestamp(),
                          updatedAt: serverTimestamp()
                        });
                      });
                    }}
                    className="btn-primary"
                  >
                    Add Sample Services
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Details Modal */}
        {showCustomerDetails && selectedCustomer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-secondary-900">Customer Details</h2>
                  <button
                    onClick={() => setShowCustomerDetails(false)}
                    className="text-secondary-400 hover:text-secondary-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700">First Name</label>
                          <p className="text-secondary-900">{selectedCustomer.firstName}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700">Last Name</label>
                          <p className="text-secondary-900">{selectedCustomer.lastName}</p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Email</label>
                        <p className="text-secondary-900">{selectedCustomer.email}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Phone</label>
                        <p className="text-secondary-900">{selectedCustomer.phone}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Address</label>
                        <p className="text-secondary-900">
                          {typeof selectedCustomer.address === 'object'
                            ? `${selectedCustomer.address.street}, ${selectedCustomer.address.city}, ${selectedCustomer.address.state} ${selectedCustomer.address.zipCode}`
                            : selectedCustomer.address
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Service Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Service Information</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700">Status</label>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedCustomer.status === 'active' ? 'bg-green-100 text-green-800' :
                            selectedCustomer.status === 'inactive' ? 'bg-red-100 text-red-800' :
                            selectedCustomer.status === 'prospect' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedCustomer.status}
                          </span>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700">Property Type</label>
                          <p className="text-secondary-900 capitalize">{selectedCustomer.propertyType}</p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Property Size</label>
                        <p className="text-secondary-900">{selectedCustomer.propertySize || 'Not specified'}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Preferred Contact</label>
                        <p className="text-secondary-900 capitalize">{selectedCustomer.preferredContact}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Source</label>
                        <p className="text-secondary-900">{selectedCustomer.source}</p>
                      </div>

                      {selectedCustomer.notes && (
                        <div>
                          <label className="block text-sm font-medium text-secondary-700">Notes</label>
                          <p className="text-secondary-900">{selectedCustomer.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Services Pricing */}
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-4">Services & Pricing</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedCustomer.services && Object.entries(selectedCustomer.services).map(([serviceType, price]) => (
                        <div key={serviceType} className="flex justify-between items-center p-3 bg-secondary-50 rounded-lg">
                          <span className="text-sm font-medium text-secondary-700">{serviceType}</span>
                          <span className="text-sm font-semibold text-green-600">${price || 'Not set'}</span>
                        </div>
                      ))}
                      {(!selectedCustomer.services || Object.keys(selectedCustomer.services).length === 0) && (
                        <div className="col-span-full text-center py-8 text-secondary-500">
                          No service pricing configured for this customer
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Statistics */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-4">Service Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="card">
                      <div className="p-4">
                        <div className="text-2xl font-bold text-primary-600">{selectedCustomer.totalServices || 0}</div>
                        <div className="text-sm text-secondary-600">Total Services</div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="p-4">
                        <div className="text-2xl font-bold text-green-600">${selectedCustomer.totalSpent || 0}</div>
                        <div className="text-sm text-secondary-600">Total Spent</div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="p-4">
                        <div className="text-lg font-semibold text-secondary-900">
                          {selectedCustomer.lastService ? new Date(selectedCustomer.lastService.seconds * 1000).toLocaleDateString() : 'Never'}
                        </div>
                        <div className="text-sm text-secondary-600">Last Service</div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="p-4">
                        <div className="text-lg font-semibold text-secondary-900">
                          {selectedCustomer.createdAt ? new Date(selectedCustomer.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                        </div>
                        <div className="text-sm text-secondary-600">Customer Since</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Services */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-secondary-900 mb-4">Recent Services</h3>
                  <div className="space-y-3">
                    {serviceHistory
                      .filter(service => service.customerId === selectedCustomer.id)
                      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
                      .slice(0, 5)
                      .map((service) => (
                        <div key={service.id} className="border border-secondary-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-secondary-900">{service.serviceType}</h4>
                              <p className="text-sm text-secondary-600 mt-1">{service.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-secondary-500">
                                <span>Status: <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  service.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  service.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                                  service.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>{service.status}</span></span>
                                <span>Date: {service.scheduledDate ? new Date(service.scheduledDate).toLocaleDateString() : 'N/A'}</span>
                                <span>Cost: ${service.cost || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    }

                    {serviceHistory.filter(service => service.customerId === selectedCustomer.id).length === 0 && (
                      <div className="text-center py-8 text-secondary-500">
                        <p>No services recorded yet.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-secondary-200">
                  <button
                    onClick={() => {
                      setEditingCustomer(selectedCustomer);
                      setCustomerForm({
                        firstName: selectedCustomer.firstName || '',
                        lastName: selectedCustomer.lastName || '',
                        email: selectedCustomer.email || '',
                        phone: selectedCustomer.phone || '',
                        address: typeof selectedCustomer.address === 'object' ? selectedCustomer.address : {
                          street: selectedCustomer.address || '',
                          city: selectedCustomer.city || '',
                          state: selectedCustomer.state || '',
                          zipCode: selectedCustomer.zipCode || ''
                        },
                        preferredContact: selectedCustomer.preferredContact || 'email',
                        status: selectedCustomer.status || 'active',
                        notes: selectedCustomer.notes || '',
                        propertyType: selectedCustomer.propertyType || 'residential',
                        propertySize: selectedCustomer.propertySize || '',
                        source: selectedCustomer.source || 'website',
                        customPricing: selectedCustomer.customPricing || {}
                      });
                      setShowCustomerDetails(false);
                      setShowForm(true);
                    }}
                    className="btn-secondary"
                  >
                    Edit Customer
                  </button>
                  <button
                    onClick={() => setShowCustomerDetails(false)}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerManagement;